import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

// ── Interfaces matching backend DTOs exactly ──

export interface AuthResponse {
    token: string;
    refreshToken: string;
    expiration: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface ApiResponse<T> {
    success: boolean;       // matches backend ApiResponse<T>.Success
    data: T;
    message: string;
    errors: string[];
}

// ── Storage keys ──
const STORAGE_KEY_USER = 'bloomie_current_user';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = environment.apiUrl + '/Auth';
    private currentUserSubject = new BehaviorSubject<AuthResponse | null>(null);
    private refreshInProgress = false;

    /** Observable stream of the currently logged-in user (null if logged out). */
    currentUser$ = this.currentUserSubject.asObservable();

    constructor(private http: HttpClient) {
        this.loadUserFromStorage();
    }

    /** Snapshot of the current user (synchronous). */
    get currentUserValue(): AuthResponse | null {
        return this.currentUserSubject.value;
    }

    // ────────────────────────────────────────
    // Login
    // ────────────────────────────────────────
    login(email: string, password: string): Observable<ApiResponse<AuthResponse>> {
        return this.http.post<ApiResponse<AuthResponse>>(`${this.apiUrl}/login`, { email, password })
            .pipe(
                tap(response => {
                    if (response?.success && response.data) {
                        this.persistUser(response.data);
                    }
                })
            );
    }

    // ────────────────────────────────────────
    // Logout (calls backend then clears state)
    // ────────────────────────────────────────
    logout(): Observable<any> {
        const token = this.getToken();
        this.clearUser();

        if (token) {
            // Fire-and-forget; even if it fails we've already cleared local state
            return this.http.post(`${this.apiUrl}/logout`, {}).pipe(
                catchError(() => of(null))
            );
        }
        return of(null);
    }

    // ────────────────────────────────────────
    // Token Refresh
    // ────────────────────────────────────────
    refreshToken(): Observable<ApiResponse<AuthResponse>> {
        const user = this.currentUserValue;
        if (!user?.token || !user?.refreshToken) {
            return throwError(() => new Error('No tokens available'));
        }

        if (this.refreshInProgress) {
            return throwError(() => new Error('Refresh already in progress'));
        }

        this.refreshInProgress = true;

        return this.http.post<ApiResponse<AuthResponse>>(`${this.apiUrl}/refresh-token`, {
            accessToken: user.token,
            refreshToken: user.refreshToken
        }).pipe(
            tap(response => {
                this.refreshInProgress = false;
                if (response?.success && response.data) {
                    this.persistUser(response.data);
                }
            }),
            catchError(err => {
                this.refreshInProgress = false;
                this.clearUser();
                return throwError(() => err);
            })
        );
    }

    // ────────────────────────────────────────
    // Token helpers
    // ────────────────────────────────────────
    getToken(): string | null {
        return this.currentUserValue?.token || null;
    }

    getRefreshToken(): string | null {
        return this.currentUserValue?.refreshToken || null;
    }

    /** Returns true if there is a stored user AND the JWT expiration hasn't passed. */
    isAuthenticated(): boolean {
        const user = this.currentUserValue;
        if (!user?.token || !user?.expiration) return false;
        return new Date(user.expiration) > new Date();
    }

    /** Returns true if there is a stored user (token may be expired but refresh may succeed). */
    hasSession(): boolean {
        return !!this.currentUserValue?.token;
    }

    // ────────────────────────────────────────
    // Private helpers
    // ────────────────────────────────────────
    private persistUser(user: AuthResponse): void {
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
        this.currentUserSubject.next(user);
    }

    private clearUser(): void {
        localStorage.removeItem(STORAGE_KEY_USER);
        this.currentUserSubject.next(null);
    }

    private loadUserFromStorage(): void {
        try {
            const stored = localStorage.getItem(STORAGE_KEY_USER);
            if (stored) {
                const user: AuthResponse = JSON.parse(stored);
                this.currentUserSubject.next(user);
            }
        } catch {
            this.clearUser();
        }
    }
}
